import {
  Button,
  Field,
  ImageGroupFieldInput,
  Modal,
  ModalProps,
} from '@payaca/ambrosia';
import { FC, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';

import useGetProjectUploads from '@/api/queries/project/useGetProjectUploads';
import { File as _File } from '@/gql/graphql';
import AddCardButton from '@payaca/components/addCard/AddCardButton';

import UntitledIcon from '@payaca/untitled-icons';
import { Link, useParams } from 'react-router-dom';
import ProjectFileCard from './ProjectFileCard';
import {
  fieldValidationState,
  isMultipleFileSelection,
} from './utils/reactHookForm';

const FILE_SIZE_LIMIT_MEGABYTES = 5;

type File = Pick<_File, 'id' | 'size'> & Partial<Omit<_File, 'id' | 'size'>>;

const isFileTooLarge = (file: File) =>
  file.size > FILE_SIZE_LIMIT_MEGABYTES * 1024 * 1024;

export interface IProps {
  name: string;
  formSection: any;
}

const ProjectFileSelection: FC<IProps> = ({ name, formSection }) => {
  /**
   * React Form Hook
   */
  const form = useFormContext();

  /**
   * Router
   */
  const { projectId } = useParams<{
    projectId: string;
    connectionApplicationId: string;
  }>();

  /**
   * Queries
   */
  const { uploads } = useGetProjectUploads(projectId, undefined, {
    offset: 0,
    limit: 100,
  });

  /**
   * State
   */
  const [isLinkFilesModalOpen, setIsLinkFilesModalOpen] = useState(false);

  const multiple = isMultipleFileSelection(formSection);

  const fieldName = multiple
    ? `${name}.projectFileIDs`
    : `${name}.projectFileID`;
  const error = get(form.formState.errors, fieldName);

  return (
    <>
      <Field.Legacy
        name={fieldName}
        validationState={error && fieldValidationState(error)}
      >
        <Controller
          control={form.control}
          name={fieldName}
          render={({ field }) => {
            const { onChange, value, disabled, ref, ...rest } = field;

            // https://react-hook-form.com/faqs#Howtosharerefusage
            const htmlRef = useRef<HTMLDivElement>(null);
            useImperativeHandle(ref, () => ({
              focus: () => {
                htmlRef.current?.scrollIntoView();
              },
            }));

            return (
              <>
                <ProjectFileCard.Wrapper ref={htmlRef}>
                  {uploads?.items
                    .filter((file) => value?.includes(file.id))
                    .map((file) => (
                      <ProjectFileCard
                        key={file.id}
                        thumbnailUrl={file.thumbnailUrl || ''}
                        fileName={file.fileName}
                        onRemove={() =>
                          onChange(
                            multiple
                              ? (value as string[]).filter(
                                  (id) => id !== file.id
                                )
                              : ''
                          )
                        }
                      />
                    ))}

                  <AddCardButton
                    disabled={disabled}
                    text={multiple ? 'Add files' : 'Add file'}
                    onClick={() => setIsLinkFilesModalOpen(true)}
                  />
                </ProjectFileCard.Wrapper>

                <ImageSelectModal
                  title={multiple ? 'Add files' : 'Add file'}
                  multiple={multiple}
                  isOpen={isLinkFilesModalOpen}
                  files={uploads?.items || []}
                  onClose={() => setIsLinkFilesModalOpen(false)}
                  initialSelectedFiles={multiple ? value : [value]}
                  onSubmit={(fileIds) => {
                    onChange(multiple ? fileIds : fileIds[0]);

                    setIsLinkFilesModalOpen(false);
                  }}
                  projectId={projectId}
                />
              </>
            );
          }}
        />
      </Field.Legacy>
    </>
  );
};

const ImageSelectModal: FC<
  Omit<ModalProps, 'title'> & {
    title?: string;
    files: File[];
    multiple?: boolean;
    onSubmit: (selectedFileIds: string[]) => void;
    initialSelectedFiles?: string[];
    projectId: string;
  }
> = (props) => {
  const {
    title = 'Select files',
    isOpen,
    onClose,
    files,
    multiple,
    onSubmit,
    initialSelectedFiles = [],
    projectId,
  } = props;

  const [selectedFileIds, setSelectedFileIds] = useState<
    string[] | undefined | null
  >(initialSelectedFiles);

  useEffect(() => {
    if (isOpen) {
      setSelectedFileIds(initialSelectedFiles);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'initialSelectedFiles'
  }, [isOpen]);

  return (
    <Modal
      size={files.length ? 'lg' : 'sm'}
      title={title}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Body>
        {files.length ? (
          <ImageGroupFieldInput.Group>
            {files.map((file) => {
              const tooLarge = isFileTooLarge(file);
              return (
                <div key={file.id} className={tooLarge ? 'opacity-70' : ''}>
                  <ImageGroupFieldInput
                    label={file.fileName}
                    src={file.thumbnailUrl || ''}
                    multiple={multiple}
                    disabled={tooLarge}
                    checked={selectedFileIds?.includes(file.id)}
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        setSelectedFileIds((prevState) =>
                          multiple
                            ? [...new Set([...(prevState || []), file.id])]
                            : [file.id]
                        );
                      } else {
                        setSelectedFileIds((prevState) =>
                          (prevState || []).filter((id) => id !== file.id)
                        );
                      }
                    }}
                  />
                  {tooLarge && (
                    <p className="mt-2 text-center text-sm text-red-800">
                      <UntitledIcon
                        name="alert-triangle.3"
                        className="text-xs align-middle"
                      />{' '}
                      File too large (limit: {FILE_SIZE_LIMIT_MEGABYTES}MB)
                    </p>
                  )}
                </div>
              );
            })}
          </ImageGroupFieldInput.Group>
        ) : (
          <p>
            No images available to select from yet. Head to the{' '}
            <Link
              to={`/projects/${projectId}/files`}
              onClick={(e) => e.stopPropagation()}
            >
              files
            </Link>{' '}
            tab on this Project to upload them.
          </p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Actions>
          {files.length ? (
            <Button onClick={() => onSubmit(selectedFileIds || [])}>
              {title}
            </Button>
          ) : (
            <Button onClick={onClose}>Ok</Button>
          )}
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectFileSelection;
